/**
 * Login page for existing caregivers
 */

import { useEffect, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { LoginForm } from '../../components/auth';
import { useToast } from '../../hooks/use-toast';

export default function Login() {
  const hasHandledVerificationToast = useRef(false);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { success, error } = useToast();
  const isSupervisorMode = searchParams.get('mode') === 'supervisor';

  useEffect(() => {
    if (hasHandledVerificationToast.current) return;

    const verificationStatus = searchParams.get('verified');
    if (verificationStatus !== '1' && verificationStatus !== '0') return;

    hasHandledVerificationToast.current = true;

    if (verificationStatus === '1') {
      success('Email confirmed. You can now log in.');
    } else {
      error('Email confirmation failed. Please request a new verification email.');
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('verified');
    setSearchParams(nextParams, { replace: true });
  }, [error, searchParams, setSearchParams, success]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-linen px-4 py-8">
      <Card className="w-full max-w-md p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-8">
            <img
              src={isSupervisorMode ? '/carecal-lilli-companion-logo.png' : '/logo.png'}
              alt={isSupervisorMode ? 'CareCal Lilli Companion' : 'CareCal'}
              className={
                isSupervisorMode
                  ? 'h-20 w-auto max-w-full'
                  : 'h-10 w-auto'
              }
            />
          </div>
          <h1 className="font-display text-3xl font-bold text-blueviolet mb-2">Welcome</h1>
          <p className="font-body text-night-500">Log in to manage your availability</p>
        </div>

        {/* Session Expired Message */}
        {location.state?.sessionExpired && (
          <div className="mb-6 p-4 bg-warning-light border border-warning rounded-lg">
            <p className="text-sm font-body text-warning-dark">
              Your session has expired. Please log in again.
            </p>
          </div>
        )}

        {/* Login Form */}
        <LoginForm />

        {/* Register Link */}
        <div className="mt-6 flex justify-center gap-4 font-body text-sm">
          <Link
            to="/auth/forgot-password"
            className="font-medium text-blueviolet hover:text-blueviolet-light text-center underline"
          >
            Reset Password
          </Link>
          <Link
            to={isSupervisorMode ? '/b2b' : '/auth/register'}
            className="font-medium text-blueviolet hover:text-blueviolet-light text-center underline"
          >
            Create New Account
          </Link>
        </div>
      </Card>
    </div>
  );
}
